import React, { useEffect, useRef, useState } from "react";
import { CheckCircle2, AlertTriangle, XCircle, HelpCircle } from "lucide-react";
import { API } from "../../lib/api";

/**
 * EdgeReleaseHealthBadge (v2.1.2-rc14)
 *
 * Display-only admin pill that polls GET /api/edge/release/health
 * every 30s and renders a coloured traffic-light state. Never
 * triggers a build / publish / repair — read-only by contract.
 *
 * States:
 *   - green   ok=true                       → "Release in sync (<ver>)"
 *   - amber   ok=true && warnings.length>0  → "Release warning"
 *   - red     ok=false (HTTP 200, ok=false) → "Release artifact problem"
 *   - grey    request failed / 403 / 500    → "Release health unavailable"
 *
 * Only renders when the current user is admin. Other roles see nothing.
 *
 * No buttons. No modals. No mutating actions. The detail panel is a
 * hover-only popover so the badge cannot interrupt normal Studio use.
 */
const POLL_INTERVAL_MS = 30_000;

const STATE_STYLE = {
  green: {
    icon: CheckCircle2,
    dot: "bg-green-500",
    ring: "ring-green-500/30",
    text: "text-green-400",
    border: "border-green-500/40",
  },
  amber: {
    icon: AlertTriangle,
    dot: "bg-amber-400",
    ring: "ring-amber-400/30",
    text: "text-amber-300",
    border: "border-amber-400/40",
  },
  red: {
    icon: XCircle,
    dot: "bg-red-500",
    ring: "ring-red-500/30",
    text: "text-red-400",
    border: "border-red-500/40",
  },
  grey: {
    icon: HelpCircle,
    dot: "bg-zinc-500",
    ring: "ring-zinc-500/30",
    text: "text-zinc-400",
    border: "border-zinc-700",
  },
};

function deriveState(report, requestFailed) {
  if (requestFailed) return "grey";
  if (!report) return "grey";
  if (report.ok === false) return "red";
  if (report.ok === true && report.warnings && report.warnings.length > 0) {
    return "amber";
  }
  if (report.ok === true) return "green";
  return "grey";
}

function deriveLabel(state, report) {
  switch (state) {
    case "green":
      return `Release in sync (${report?.source_version || "unknown"})`;
    case "amber":
      return "Release warning";
    case "red":
      // v4.12.1 -- surface the specific artifact + expected vs
      // discovered so operators do not have to hover to know what
      // broke. Falls back to the generic label only when the report
      // does not carry enough diagnostic detail.
      return _redSpecificLabel(report) || "Release artifact problem";
    case "grey":
    default:
      return "Release health unavailable";
  }
}

/**
 * Build a concise "<artifact> expected <exp>, discovered <got>" label
 * from the report so the badge itself explains the mismatch. Returns
 * null when the report shape doesn't give us enough to be specific --
 * the caller then keeps the generic label.
 *
 * IMPORTANT (v4.12.1 correction): the report describes the
 * ``edge.zip`` artifact produced by ``edge-package/scripts/build_release.sh``.
 * That artifact reports version numbers on the ``2.1.2-rcXX`` line
 * (it also carries a ``LEGACY_VERSION=1.24.22`` compat anchor
 * internally). The stable Edge Server fleet, per operator, is on the
 * INDEPENDENT ``2.1.14`` numeric line. This helper therefore labels
 * the mismatch as "Legacy Edge Server package" -- it is NOT the
 * Edge Relay installer (the ``v1.24.22`` label historically applied
 * to this same artifact is a compat anchor, not a separate
 * downloadable). Do NOT relabel this as "Edge Relay" without proof
 * that a separately-versioned Edge Relay installer exists.
 *
 * The Edge release-health report carries four versions:
 *   source_version           -- edge-package/railstream_edge/version.py
 *   latest_zip_version       -- RELEASE_VERSION inside edge.zip
 *   versioned_zip_version    -- RELEASE_VERSION inside edge-v<VER>.zip
 *   release_json_version     -- version field of edge-release.json
 *
 * Any disagreement between source and a served artifact is a red;
 * this helper picks the FIRST mismatch and reports both sides.
 */
function _redSpecificLabel(report) {
  if (!report || !report.source_version) return null;
  const src = report.source_version;
  const checks = [
    ["edge.zip", report.latest_zip_version],
    ["edge-v<VER>.zip", report.versioned_zip_version],
    ["release.json", report.release_json_version],
  ];
  for (const [name, got] of checks) {
    if (got && got !== src) {
      return `Legacy Edge Server package mismatch: ${name} v${got} vs source v${src}`;
    }
  }
  // No version mismatch -- must be a sha / byte-identity mismatch.
  if (report.latest_sha_ok === false) {
    return `Legacy Edge Server package sha256 mismatch on edge.zip (source v${src})`;
  }
  if (report.versioned_sha_ok === false) {
    return `Legacy Edge Server package sha256 mismatch on edge-v${src}.zip`;
  }
  if (report.latest_matches_versioned === false) {
    return `Legacy Edge Server package: edge.zip ≠ edge-v${src}.zip (byte content differs)`;
  }
  return null;
}

function deriveDetail(state, report) {
  if (state === "red") {
    return (report?.errors && report.errors[0]) || "Unknown error";
  }
  if (state === "amber") {
    return (report?.warnings && report.warnings[0]) || "Unknown warning";
  }
  return null;
}

export const EdgeReleaseHealthBadge = ({ api, user }) => {
  const [report, setReport] = useState(null);
  const [requestFailed, setRequestFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);

  const isAdmin = (user?.role === "admin" || user?.role === "super_admin");

  useEffect(() => {
    if (!isAdmin || !api) return undefined;

    let cancelled = false;
    const poll = async () => {
      try {
        const res = await api.get("/edge/release/health");
        if (cancelled) return;
        // HTTP 200 with ok=false is a valid red — NOT a network
        // failure. Only mark the request as failed on a real
        // transport error or non-2xx response.
        setReport(res.data || null);
        setRequestFailed(false);
        setLoaded(true);
      } catch (_e) {
        if (cancelled) return;
        setReport(null);
        setRequestFailed(true);
        setLoaded(true);
      }
    };

    poll();
    timerRef.current = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [api, isAdmin]);

  if (!isAdmin) return null;
  // Do not show a false grey warning while the first read-only health
  // request is still in flight. "Unavailable" now means the request
  // actually failed, not merely that React rendered before it finished.
  if (!loaded) return null;
  // A retained legacy download must not create a global red production
  // alarm. Its mismatch remains documented on System & Releases, while
  // the sidebar is reserved for problems that affect the current fleet.
  if (report?.scope === "legacy_edge_server_package" && report?.affects_current_fleet === false) {
    return null;
  }

  const state = deriveState(report, requestFailed);
  const style = STATE_STYLE[state];
  const Icon = style.icon;
  const label = deriveLabel(state, report);
  const detail = deriveDetail(state, report);

  return (
    <div
      className="relative"
      data-testid="edge-release-health-badge"
      data-state={state}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      <div
        tabIndex={0}
        className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full border text-xs ${style.border} ${style.text} bg-zinc-900/60`}
        aria-label={label}
      >
        <span
          className={`w-2 h-2 rounded-full ${style.dot} ring-2 ${style.ring}`}
          data-testid="edge-release-health-dot"
        />
        <Icon className="w-3.5 h-3.5" />
        <span data-testid="edge-release-health-label" className="truncate max-w-[14rem]">
          {label}
        </span>
      </div>

      {open && (
        <div
          data-testid="edge-release-health-detail"
          className="absolute right-0 mt-2 z-50 w-72 rounded-md border border-zinc-800 bg-zinc-950/95 backdrop-blur p-3 shadow-2xl text-xs text-zinc-300 space-y-1.5"
          role="tooltip"
        >
          <div className="flex items-center gap-2 text-sm font-medium text-white">
            <span className={`w-2 h-2 rounded-full ${style.dot}`} />
            <span>Edge release health</span>
          </div>
          {detail && (
            <p
              data-testid="edge-release-health-detail-message"
              className={`${style.text} text-xs leading-snug`}
            >
              {detail}
            </p>
          )}
          <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-0.5 text-zinc-400">
            <dt>Source</dt>
            <dd data-testid="edge-release-health-source" className="text-zinc-200">
              {report?.source_version || "—"}
            </dd>
            <dt>edge.zip</dt>
            <dd data-testid="edge-release-health-latest" className="text-zinc-200">
              {report?.latest_zip_version || "—"}
            </dd>
            <dt>edge-v&lt;VER&gt;.zip</dt>
            <dd data-testid="edge-release-health-versioned" className="text-zinc-200">
              {report?.versioned_zip_version || "—"}
            </dd>
            <dt>release.json</dt>
            <dd data-testid="edge-release-health-releasejson" className="text-zinc-200">
              {report?.release_json_version || "—"}
            </dd>
            <dt>Artifact</dt>
            <dd
              data-testid="edge-release-health-artifact"
              className="text-zinc-200 break-all"
            >
              {report?.served_artifact || "—"}
            </dd>
          </dl>
          <p className="pt-1 text-[10px] text-zinc-500">
            Read-only. Re-run{" "}
            <code className="text-zinc-300">
              edge-package/scripts/build_release.sh --publish
            </code>{" "}
            on a red.
          </p>
        </div>
      )}
    </div>
  );
};

export default EdgeReleaseHealthBadge;

// --- testing surface ---
// Exposed only so unit tests can exercise the pure derivation
// functions without spinning up the full component. Not part of the
// public component API.
export const __test__ = { deriveState, deriveLabel, deriveDetail, _redSpecificLabel };
// Backend URL is sourced from REACT_APP_BACKEND_URL via lib/api.js
export const __API__ = API;
